import { useTranslation } from "react-i18next"
import { SyntheticEvent, useEffect, useMemo, useState } from "react"

import {
  useCreatePlayerGroupMutation,
  useUpsertPlayerGroupMutation,
} from "@/hooks/api/useSettingQuery"
import Dialog from "@/components/dialog/Dialog"
import BasicButton from "@/components/button/BasicButton"
import TextField from "@/components/form/textField/TextField"
import { createGlobalStore } from "@/stores/global"

import "./PlayerGroupManageDialog.scss"

export default function PlayerGroupManageDialog({
  open,
  title,
  idfTeamPlayerGroup,
  defaultValue,
  onClose,
  handleChange,
}: {
  open: boolean
  title: string
  idfTeamPlayerGroup: number
  defaultValue: string
  onClose: () => void
  handleChange: (event?: SyntheticEvent<Element, Event>, newValue?: string | number) => void
}) {
  const { t } = useTranslation("")
  const setSnackBar = createGlobalStore((state) => state.setSnackBar)
  const [inputText, setInputText] = useState("")
  const [isFirstRender, setIsFirstRender] = useState(true)

  const { mutateAsync: createPlayerGroupAsync, isSuccess: isCreateSuccess } =
    useCreatePlayerGroupMutation()
  const { mutate: upsertPlayerGroup, isSuccess: isUpsertSuccess } = useUpsertPlayerGroupMutation()

  useEffect(() => {
    if (defaultValue) {
      setInputText(defaultValue)
    } else {
      setIsFirstRender(true)
    }
  }, [defaultValue, open])

  useEffect(() => {
    if (isCreateSuccess || isUpsertSuccess) {
      setSnackBar({ open: true, message: t("messages.successUpdate"), severity: "success" })
    }
  }, [isCreateSuccess, isUpsertSuccess])

  const handleClose = () => {
    setInputText("")
    onClose()
  }

  // 앞뒤 공백을 뗀 이름이 검증·저장의 기준이다. 공백만 입력한 경우는 빈 이름으로 본다 (#173)
  const trimmedName = inputText.trim()

  const onSave = async () => {
    if (title === "newGroup") {
      const res = await createPlayerGroupAsync({ name: trimmedName })
      if (res.success) {
        handleChange()
        setSnackBar({
          message: t("messages.successUpdate"),
          severity: "success",
          open: true,
        })
      } else {
        return
      }
    } else if (title === "editGroup") {
      upsertPlayerGroup({ name: trimmedName, idfTeamPlayerGroup })
    }
    handleClose()
  }

  const isErrorNameInput = useMemo(() => {
    const isEmptyInput = trimmedName.length === 0
    const isOverMaxLength = trimmedName.length > 35

    return (!isFirstRender && isEmptyInput) || isOverMaxLength
  }, [trimmedName, isFirstRender])

  const nameErrorMessage = useMemo(() => {
    if (trimmedName.length === 0) {
      return t("rules.required")
    }
    if (trimmedName.length > 35) {
      return t("rules.max_len", { name: t("groupName"), max_len: 35 })
    }

    return ""
  }, [trimmedName, t])

  const isDisabledSaveBtn = useMemo(() => {
    const isEmptyInput = trimmedName.length === 0
    const isOverMaxLength = trimmedName.length > 35
    const isSameDefaultValue = defaultValue.trim() === trimmedName

    return isEmptyInput || isOverMaxLength || isSameDefaultValue
  }, [trimmedName, defaultValue])

  return (
    <Dialog
      className="PlayerGroupManageDialogCont"
      title={t(title)}
      open={open}
      onClose={handleClose}
      maxWidth="600px"
      footer={
        <>
          <BasicButton type="default-outlined" size="md" onClick={handleClose}>
            {t("button.cancel")}
          </BasicButton>
          <BasicButton
            type="primary-contained"
            size="md"
            onClick={onSave}
            disabled={isDisabledSaveBtn}>
            {t("button.save")}
          </BasicButton>
        </>
      }>
      <div className="GroupNameInputCont">
        {t("groupName")}
        <TextField
          placeholder={t("messages.enterGroupName")}
          value={inputText}
          fullWidth
          onChange={(e) => setInputText((e.target as HTMLInputElement).value)}
          error={isErrorNameInput}
          showMessage={isErrorNameInput}
          message={nameErrorMessage}
          onBlur={() => setIsFirstRender(false)}
        />
      </div>
    </Dialog>
  )
}
